// How the 1440x792 room design maps onto any window (D_Desk scales one fixed board; a real window has any shape).
//   picture  the room picture and her clips cover the window. Her paper card is drawn in the picture's coordinates, so
//            it always lies on the white card in the painting. When the window crops the picture, the crop keeps that
//            card in view and clear of the subtitles, and her head in frame: a window much wider than the picture
//            (2560x1080, a maximized browser at 1920x960) gets the picture just tall enough for both, and its sides are
//            filled (RoomMedia: a soft, blurred copy of the room) instead of cutting her off.
//   ui       everything else uses the design's own numbers (right 40, top 74, ...) in a canvas of (W/u) x (H/u) scaled
//            by u = min(W/1440, H/792), clamped so text never gets tiny (min window 1000x640) or huge.
import type { CharId } from '@shared/types';
import { PIC, ROOM, STAGE } from './roomCopy';

export interface RoomFit {
  W: number; H: number;
  /** UI scale and the UI canvas size in design px. */
  u: number; vw: number; vh: number;
  /** Picture scale and its top-left corner in window px. */
  p: number; px: number; py: number;
  /** The picture is narrower than the window: RoomMedia fills the sides. */
  sides: boolean;
}

export const UI_MIN = 0.78;
export const UI_MAX = 1.5;
/** Subtitles column (design px): right 40, width 392 (D_Desk). */
export const COL = { right: 40, width: 392 };
/** Below her card: the paper stack under it. */
const CARD_FOOT = 14;

export function roomFit(W: number, H: number, ch: CharId): RoomFit {
  const w = Math.max(1, W), h = Math.max(1, H);
  const u = Math.max(UI_MIN, Math.min(UI_MAX, Math.min(w / STAGE.w, h / STAGE.h)));
  const vw = w / u, vh = h / u;
  const R = ROOM[ch];
  const top = R.head, bottom = R.by + CARD_FOOT;
  // Cover, but never so big that her head and her card can't both be in the window.
  const p = Math.min(Math.max(w / PIC.w, h / PIC.h), h / (bottom - top));
  const pw = PIC.w * p, ph = PIC.h * p;
  const sides = pw < w - 0.5;
  // Horizontal: centered, but never so far right that her card slides under the subtitles column.
  const colLeft = w - (COL.right + COL.width) * u;
  let px = (w - pw) / 2;
  px = Math.min(px, colLeft - 24 * u - (R.cx + 170) * p);
  px = sides ? Math.max(0, Math.min(w - pw, px)) : Math.max(w - pw, Math.min(0, px));
  // Vertical: like D_Desk's object-position 50% 42%, but the bottom of her card and the top of her head stay on screen.
  let py = (h - ph) * 0.42;
  py = Math.max(-top * p, Math.min(h - bottom * p, py));
  py = Math.max(h - ph, Math.min(0, py));
  return { W: w, H: h, u, vw, vh, p, px, py, sides };
}
